import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
import config from '../config/api';
import {
  EyeIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ServerIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Hosting = () => {
  const [hostingItems, setHostingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterType, setFilterType] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');

  useEffect(() => {
    fetchHostingItems();
  }, []);

  const fetchHostingItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(config.HOSTING_URL, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHostingItems(data);
      }
    } catch (error) {
      console.error('Error fetching hosting items:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toUpperCase();
    const colors = {
      'ACTIVE': 'text-green-600 bg-green-100',
      'INACTIVE': 'text-gray-600 bg-gray-100',
      'EXPIRED': 'text-red-600 bg-red-100',
      'CANCELLED': 'text-red-600 bg-red-100',
      'SUSPENDED': 'text-yellow-600 bg-yellow-100'
    };
    return colors[normalizedStatus] || 'text-gray-600 bg-gray-100';
  };

  const getStatusIcon = (status) => {
    const normalizedStatus = status?.toUpperCase();
    switch (normalizedStatus) {
      case 'ACTIVE':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'EXPIRED':
      case 'CANCELLED':
        return <XCircleIcon className="w-4 h-4" />;
      case 'SUSPENDED':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const getTypeIcon = (type) => {
    const normalizedType = type?.toUpperCase();
    switch (normalizedType) {
      case 'HOSTING':
        return <ServerIcon className="w-5 h-5" />;
      case 'DOMAIN':
        return <GlobeAltIcon className="w-5 h-5" />;
      default:
        return <ServerIcon className="w-5 h-5" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (!dateString) return '';
    return dayjs.utc(dateString).tz('Asia/Ho_Chi_Minh').format('DD/MM/YYYY HH:mm') + ' GMT+7';
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryWarning = (expiryDate) => {
    const daysUntilExpiry = getDaysUntilExpiry(expiryDate);
    if (daysUntilExpiry === null) return null;
    
    if (daysUntilExpiry < 0) {
      return { type: 'expired', message: `Expired ${Math.abs(daysUntilExpiry)} days ago`, color: 'text-red-600' };
    } else if (daysUntilExpiry <= 30) {
      return { type: 'warning', message: `Expires in ${daysUntilExpiry} days`, color: 'text-orange-600' };
    }
    return null;
  };

  const filteredItems = hostingItems.filter(item => {
    const typeMatch = filterType === 'ALL' || item.type === filterType;
    const statusMatch = filterStatus === 'ALL' || item.status === filterStatus;
    return typeMatch && statusMatch;
  });

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Hosting & Tên miền</h1>
        <div className="flex items-center space-x-4">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Tất cả loại</option>
            <option value="hosting">HOSTING</option>
            <option value="domain">DOMAIN</option>
            <option value="ssl">SSL</option>
            <option value="email">EMAIL</option>
            <option value="other">KHÁC</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Tất cả trạng thái</option>
            <option value="active">Đang hoạt động</option>
            <option value="expired">Hết hạn</option>
            <option value="cancelled">Đã hủy</option>
          </select>
        </div>
      </div>

      {/* Hosting Items Grid */}
      {filteredItems.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ServerIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Không tìm thấy dịch vụ hosting</h3>
          <p className="text-gray-600">
            {filterType === 'ALL' && filterStatus === 'ALL'
              ? "Bạn chưa có dịch vụ hosting hoặc tên miền nào."
              : "Không có mục nào phù hợp với bộ lọc đã chọn."
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => {
            const expiryWarning = getExpiryWarning(item.expiry_date);
            
            return (
              <div key={item.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${item.type === 'HOSTING' ? 'bg-blue-100' : 'bg-green-100'}`}>
                        {getTypeIcon(item.type)}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                          {item.name || 'Không có tên'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {item.type === 'hosting' && 'HOSTING'}
                          {item.type === 'domain' && 'DOMAIN'}
                          {item.type === 'ssl' && 'SSL'}
                          {item.type === 'email' && 'EMAIL'}
                          {item.type === 'other' && 'KHÁC'}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status?.toUpperCase())}`}>
                      {getStatusIcon(item.status?.toUpperCase())}
                      <span className="ml-1">
                        {item.status === 'active' && 'HOẠT ĐỘNG'}
                        {item.status === 'expired' && 'HẾT HẠN'}
                        {item.status === 'cancelled' && 'ĐÃ HỦY'}
                      </span>
                    </span>
                  </div>

                  {/* Domain/Service Details */}
                  <div className="mb-4">
                    {item.name && (
                      <div className="bg-gray-50 p-3 rounded-lg mb-3">
                        <div className="text-sm text-gray-600">Tên dịch vụ</div>
                        <div className="font-medium text-gray-900">{item.name}</div>
                      </div>
                    )}
                    
                    {item.provider && (
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Nhà cung cấp:</span> {item.provider}
                      </div>
                    )}

                    {item.supplier && (
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Supplier:</span> {item.supplier.name}
                      </div>
                    )}

                    {item.cost && (
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Chi phí:</span> {new Intl.NumberFormat('vi-VN', {
                          style: 'currency',
                          currency: 'VND'
                        }).format(item.cost)}
                      </div>
                    )}
                  </div>

                  {/* Expiry Warning */}
                  {expiryWarning && (
                    <div className={`flex items-center p-2 rounded-lg mb-4 ${
                      expiryWarning.type === 'expired' ? 'bg-red-50' : 'bg-orange-50'
                    }`}>
                      <ExclamationTriangleIcon className={`w-4 h-4 mr-2 ${expiryWarning.color}`} />
                      <span className={`text-sm font-medium ${expiryWarning.color}`}>
                        {expiryWarning.message}
                      </span>
                    </div>
                  )}

                  {/* Dates */}
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    {item.registration_date && (
                      <div className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        <span>Đăng ký: {formatDate(item.registration_date)}</span>
                      </div>
                    )}
                    {item.expiry_date && (
                      <div className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        <span>Hết hạn: {formatDate(item.expiry_date)}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewDetails(item)}
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <EyeIcon className="w-4 h-4 mr-1" />
                      Xem chi tiết
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Details Modal */}
      {showModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-bold text-gray-900">Chi tiết dịch vụ</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <div className="flex items-center mb-3">
                    <div className={`p-2 rounded-lg mr-3 ${selectedItem.type === 'hosting' ? 'bg-blue-100' : 'bg-green-100'}`}>
                      {getTypeIcon(selectedItem.type?.toUpperCase())}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedItem.name || 'Không có tên'}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">
                          {selectedItem.type === 'hosting' && 'Hosting'}
                          {selectedItem.type === 'domain' && 'Tên miền'}
                          {selectedItem.type === 'ssl' && 'SSL Certificate'}
                          {selectedItem.type === 'email' && 'Email Hosting'}
                          {selectedItem.type === 'other' && 'Khác'}
                          {!selectedItem.type && 'Chưa xác định'}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedItem.status?.toUpperCase())}`}>
                          {getStatusIcon(selectedItem.status?.toUpperCase())}
                          <span className="ml-1">
                            {selectedItem.status === 'active' && 'Đang hoạt động'}
                            {selectedItem.status === 'expired' && 'Hết hạn'}
                            {selectedItem.status === 'cancelled' && 'Đã hủy'}
                            {!selectedItem.status && 'Chưa xác định'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Service Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedItem.name && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Tên dịch vụ</div>
                      <div className="font-medium text-gray-900">{selectedItem.name}</div>
                    </div>
                  )}
                  {selectedItem.provider && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Nhà cung cấp</div>
                      <div className="font-medium text-gray-900">{selectedItem.provider}</div>
                    </div>
                  )}
                  {selectedItem.supplier && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Supplier</div>
                      <div className="font-medium text-gray-900">{selectedItem.supplier.name}</div>
                      <div className="text-xs text-gray-500">{selectedItem.supplier.type}</div>
                    </div>
                  )}
                  {selectedItem.customer && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Khách hàng</div>
                      <div className="font-medium text-gray-900">{selectedItem.customer.company_name}</div>
                      {selectedItem.customer.contact_email && (
                        <div className="text-xs text-gray-500">{selectedItem.customer.contact_email}</div>
                      )}
                    </div>
                  )}
                  {selectedItem.project && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Dự án</div>
                      <div className="font-medium text-gray-900">{selectedItem.project.name}</div>
                      {selectedItem.project.description && (
                        <div className="text-xs text-gray-500">{selectedItem.project.description}</div>
                      )}
                    </div>
                  )}
                  {selectedItem.cost && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Chi phí</div>
                      <div className="font-medium text-gray-900">
                        {new Intl.NumberFormat('vi-VN', {
                          style: 'currency',
                          currency: 'VND'
                        }).format(selectedItem.cost)}
                      </div>
                    </div>
                  )}
                  {selectedItem.auto_renew !== undefined && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Gia hạn tự động</div>
                      <div className="font-medium text-gray-900">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          selectedItem.auto_renew 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedItem.auto_renew ? 'Bật' : 'Tắt'}
                        </span>
                      </div>
                    </div>
                  )}
                  {selectedItem.type && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Loại dịch vụ</div>
                      <div className="font-medium text-gray-900">
                        {selectedItem.type === 'hosting' && 'Hosting'}
                        {selectedItem.type === 'domain' && 'Tên miền'}
                        {selectedItem.type === 'ssl' && 'SSL Certificate'}
                        {selectedItem.type === 'email' && 'Email Hosting'}
                        {selectedItem.type === 'other' && 'Khác'}
                      </div>
                    </div>
                  )}
                </div>

                {/* Dates */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Thông tin ngày tháng</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedItem.registration_date && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-600">Ngày đăng ký</div>
                        <div className="font-medium">{formatDate(selectedItem.registration_date)}</div>
                      </div>
                    )}
                    {selectedItem.expiry_date && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-600">Ngày hết hạn</div>
                        <div className="font-medium">{formatDate(selectedItem.expiry_date)}</div>
                        {(() => {
                          const daysUntilExpiry = getDaysUntilExpiry(selectedItem.expiry_date);
                          if (daysUntilExpiry !== null) {
                            if (daysUntilExpiry < 0) {
                              return <div className="text-xs text-red-600 mt-1">Đã hết hạn {Math.abs(daysUntilExpiry)} ngày trước</div>;
                            } else if (daysUntilExpiry <= 30) {
                              return <div className="text-xs text-orange-600 mt-1">Hết hạn trong {daysUntilExpiry} ngày</div>;
                            } else {
                              return <div className="text-xs text-green-600 mt-1">Còn {daysUntilExpiry} ngày</div>;
                            }
                          }
                          return null;
                        })()}
                      </div>
                    )}
                    {selectedItem.created_at && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-600">Ngày tạo</div>
                        <div className="font-medium">{formatDate(selectedItem.created_at)}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Technical Details */}
                {(selectedItem.disk_space || selectedItem.bandwidth || selectedItem.email_accounts) && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Technical Specifications</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {selectedItem.disk_space && (
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <div className="text-sm text-blue-600">Disk Space</div>
                          <div className="font-medium text-blue-900">{selectedItem.disk_space}</div>
                        </div>
                      )}
                      {selectedItem.bandwidth && (
                        <div className="bg-green-50 p-3 rounded-lg">
                          <div className="text-sm text-green-600">Bandwidth</div>
                          <div className="font-medium text-green-900">{selectedItem.bandwidth}</div>
                        </div>
                      )}
                      {selectedItem.email_accounts && (
                        <div className="bg-purple-50 p-3 rounded-lg">
                          <div className="text-sm text-purple-600">Email Accounts</div>
                          <div className="font-medium text-purple-900">{selectedItem.email_accounts}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedItem.notes && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedItem.notes}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hosting;
